import React, { useState, useEffect } from "react";
import { useHistory, Link } from "react-router-dom";
import config from "../config";
import { buttonClass } from "../components/ui/Button";
import { Field, TextArea } from "../components/ui/Field";
import { Select } from "../components/ui/Select";
import { FileField } from "../components/ui/FileField";
import { Alert } from "../components/ui/Alert";
import Reveal from "../components/ui/Reveal";
import { EmptyState } from "../components/ui/EmptyState";
import { LogoSpinner } from "../components/ui/LogoSpinner";
import { Panel } from "../components/ui/Panel";

const RMASubmit = () => {
    const history = useHistory();

    // Catalog data from API
    const [catalog, setCatalog] = useState([]);
    const [loadingCatalog, setLoadingCatalog] = useState(true);

    // Form state
    const [customerInfo, setCustomerInfo] = useState({
        customer_name: "",
        customer_email: "",
        organization: "",
        customer_phone: "",
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        zip: "",
        country: "USA"
    });

    // Items being submitted
    const [items, setItems] = useState([]);

    // UI state
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [uploadingLogs, setUploadingLogs] = useState(false);
    const [logUploadStatus, setLogUploadStatus] = useState({}); // { [itemId]: 'uploading' | 'success' | 'error' }

    // Issue categories by item type
    const issueCategoriesByType = {
        drone: [
            { value: "", label: "Select category..." },
            { value: "flight_issue", label: "Flight Issue" },
            { value: "led_issue", label: "LED/Light Issue" },
            { value: "motor_issue", label: "Motor Issue" },
            { value: "camera_issue", label: "Camera Issue" },
            { value: "gps_issue", label: "GPS Issue" },
            { value: "frame_damage", label: "Frame Damage" },
            { value: "software_issue", label: "Software Issue" },
            { value: "other", label: "Other" }
        ],
        battery: [
            { value: "", label: "Select category..." },
            { value: "not_charging", label: "Not Charging" },
            { value: "swollen", label: "Swollen/Puffed" },
            { value: "low_capacity", label: "Reduced Capacity" },
            { value: "physical_damage", label: "Physical Damage" },
            { value: "connector_issue", label: "Connector Issue" },
            { value: "other", label: "Other" }
        ],
        charger: [
            { value: "", label: "Select category..." },
            { value: "not_powering_on", label: "Not Powering On" },
            { value: "not_charging", label: "Not Charging Batteries" },
            { value: "display_issue", label: "Display Issue" },
            { value: "port_issue", label: "Port/Connector Issue" },
            { value: "overheating", label: "Overheating" },
            { value: "other", label: "Other" }
        ],
        ground_station: [
            { value: "", label: "Select category..." },
            { value: "connectivity", label: "Connectivity Issue" },
            { value: "stick_issue", label: "Stick/Gimbal Issue" },
            { value: "physical_damage", label: "Physical Damage" },
            { value: "other", label: "Other" }
        ],
        other: [
            { value: "", label: "Select category..." },
            { value: "defective", label: "Defective" },
            { value: "physical_damage", label: "Physical Damage" },
            { value: "other", label: "Other" }
        ]
    };

    // Get issue categories for a specific item type
    const getIssueCategories = (itemType) => {
        return issueCategoriesByType[itemType] || issueCategoriesByType.other;
    };

    // Fetch catalog on mount
    useEffect(() => {
        fetchCatalog();
    }, []);

    const fetchCatalog = async () => {
        try {
            const response = await fetch(`${config.API_ENDPOINT}/rma-public/catalog`);
            const data = await response.json();
            setCatalog(data.items || []);
        } catch (err) {
            console.error("Error fetching catalog:", err);
            setError("Failed to load item catalog. Please refresh the page.");
        } finally {
            setLoadingCatalog(false);
        }
    };

    const handleCustomerChange = (e) => {
        const { name, value } = e.target;
        setCustomerInfo(prev => ({ ...prev, [name]: value }));
    };

    // Add a new item to the list
    const addItem = () => {
        setItems(prev => [...prev, {
            id: Date.now(), // temporary ID for React key
            catalog_item_id: "",
            serial_number: "",
            quantity: 1,
            issue_category: "",
            issue_description: "",
            log_file: null, // Flight log for drones
            charger_id: "", // For smart chargers (4 hex digits)
            // Validation state
            serialValidating: false,
            serialValid: null,
            serialError: null
        }]);
    };

    // Remove an item
    const removeItem = (itemId) => {
        setItems(prev => prev.filter(item => item.id !== itemId));
    };

    // Update an item field
    const updateItem = (itemId, field, value) => {
        setItems(prev => prev.map(item => {
            if (item.id !== itemId) return item;

            const updated = { ...item, [field]: value };

            // Reset serial validation when catalog item or serial changes
            if (field === 'catalog_item_id' || field === 'serial_number') {
                updated.serialValid = null;
                updated.serialError = null;
            }

            // Validate charger ID format (4 hex digits)
            if (field === 'charger_id') {
                updated.charger_id = value.toUpperCase().replace(/[^0-9A-F]/g, '').slice(0, 4);
            }

            return updated;
        }));
    };

    // Handle log file selection for an item
    const handleItemLogFile = (itemId, file) => {
        if (file && file.name.toLowerCase().endsWith('.ulg')) {
            setItems(prev => prev.map(item =>
                item.id === itemId ? { ...item, log_file: file } : item
            ));
        }
    };

    // Get catalog item details
    const getCatalogItem = (catalogItemId) => {
        return catalog.find(c => c.id === parseInt(catalogItemId));
    };

    // Validate serial number for an item
    const validateSerial = async (itemId) => {
        const item = items.find(i => i.id === itemId);
        if (!item || !item.serial_number.trim()) return;

        const catalogItem = getCatalogItem(item.catalog_item_id);
        if (!catalogItem || !catalogItem.requires_serial_number) return;

        setItems(prev => prev.map(i =>
            i.id === itemId ? { ...i, serialValidating: true, serialError: null } : i
        ));

        try {
            // For drones, validate against dronefleet
            if (catalogItem.item_type === 'drone') {
                const response = await fetch(
                    `${config.API_ENDPOINT}/rma-public/validate-serial/${encodeURIComponent(item.serial_number.trim().toUpperCase())}`
                );
                const data = await response.json();

                setItems(prev => prev.map(i =>
                    i.id === itemId ? {
                        ...i,
                        serialValidating: false,
                        serialValid: data.valid,
                        serialError: data.valid ? null : (data.message || "Serial not found")
                    } : i
                ));
            } else {
                // For non-drones, just accept the serial
                setItems(prev => prev.map(i =>
                    i.id === itemId ? { ...i, serialValidating: false, serialValid: true } : i
                ));
            }
        } catch (err) {
            setItems(prev => prev.map(i =>
                i.id === itemId ? {
                    ...i,
                    serialValidating: false,
                    serialError: "Error validating serial"
                } : i
            ));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // Validate customer info
        if (!customerInfo.customer_name.trim() || !customerInfo.customer_email.trim()) {
            setError("Please enter your name and email address");
            return;
        }

        // Validate items
        if (items.length === 0) {
            setError("Please add at least one item");
            return;
        }

        // Validate each item
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const catalogItem = getCatalogItem(item.catalog_item_id);

            if (!item.catalog_item_id) {
                setError(`Item ${i + 1}: Please select an item type`);
                return;
            }

            if (!item.issue_description.trim()) {
                setError(`Item ${i + 1}: Please describe the issue`);
                return;
            }

            if (catalogItem?.requires_serial_number && !item.serial_number.trim()) {
                setError(`Item ${i + 1} (${catalogItem.name}): Serial number is required`);
                return;
            }

            if (catalogItem?.item_type === 'drone' && item.serialValid !== true) {
                setError(`Item ${i + 1}: Please validate the drone serial number`);
                return;
            }

            if (!catalogItem?.requires_serial_number && item.quantity < 1) {
                setError(`Item ${i + 1} (${catalogItem.name}): Quantity must be at least 1`);
                return;
            }

            // Validate charger ID for chargers
            if (catalogItem?.item_type === 'charger' && item.charger_id.length !== 4) {
                setError(`Item ${i + 1} (${catalogItem.name}): Charger ID must be exactly 4 hex digits`);
                return;
            }
        }

        setSubmitting(true);

        try {
            const response = await fetch(`${config.API_ENDPOINT}/rma-public/submit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...customerInfo,
                    items: items.map(item => ({
                        catalog_item_id: parseInt(item.catalog_item_id),
                        serial_number: item.serial_number || null,
                        quantity: item.quantity,
                        issue_category: item.issue_category || null,
                        issue_description: item.issue_description,
                        charger_id: item.charger_id || null
                    }))
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Failed to submit RMA request");
            }

            // Upload any flight logs for drone items
            const logsToUpload = items.filter(item => item.log_file && item.serial_number);
            if (logsToUpload.length > 0) {
                setUploadingLogs(true);
                setSubmitting(false); // Change button text

                for (const item of logsToUpload) {
                    setLogUploadStatus(prev => ({ ...prev, [item.id]: 'uploading' }));
                    try {
                        const formData = new FormData();
                        formData.append('file', item.log_file);
                        formData.append('rma_number', data.rma.rma_number);
                        formData.append('email', customerInfo.customer_email);
                        formData.append('serial_number', item.serial_number);

                        const response = await fetch(`${config.API_ENDPOINT}/rma-public/upload-log`, {
                            method: 'POST',
                            body: formData
                        });

                        if (response.ok) {
                            setLogUploadStatus(prev => ({ ...prev, [item.id]: 'success' }));
                        } else {
                            setLogUploadStatus(prev => ({ ...prev, [item.id]: 'error' }));
                        }
                    } catch (err) {
                        console.error('Error uploading log for', item.serial_number, err);
                        setLogUploadStatus(prev => ({ ...prev, [item.id]: 'error' }));
                    }
                }

                // Brief pause to show completion status
                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            // Redirect to status page
            history.push(`/rma/status?rma=${data.rma.rma_number}&email=${encodeURIComponent(customerInfo.customer_email)}`);
        } catch (err) {
            console.error("RMA submit error:", err);
            setError(err.message);
        } finally {
            setSubmitting(false);
            setUploadingLogs(false);
        }
    };

    if (loadingCatalog) {
        return (
            <div className="flex items-center justify-center py-10 min-h-screen">
                <LogoSpinner label="Loading the RMA form" />
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center py-10 min-h-screen"
            style={{ fontFamily: "RobotoMono, sans-serif" }}
        >
            <form
                onSubmit={handleSubmit}
                className="w-11/12 max-w-3xl p-6 md:p-8 bg-[#2a2a2a] text-white
                    rounded-lg border-2 border-primary/30"
                style={{
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5), 0 0 40px rgba(255, 204, 51, 0.08)"
                }}
            >
                <h3 className="text-center text-xl font-bold mb-2 text-primary">
                    Submit RMA Request
                </h3>
                <p className="text-center text-on-surface-variant mb-6 text-sm">
                    Fill out this form to request a return merchandise authorization
                </p>

                <Reveal show={!!error}>
                    <Alert className="mb-4">{error}</Alert>
                </Reveal>

                {/* Customer Info Section */}
                <div className="border-b border-gray-700 pb-4 mb-4">
                    <h4 className="text-sm font-medium text-on-surface-variant mb-3">Contact Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block mb-1 text-sm">
                                Full Name <span className="text-red-400">*</span>
                            </label>
                            <Field
                                type="text"
                                name="customer_name"
                                value={customerInfo.customer_name}
                                onChange={handleCustomerChange}
                                required
                            />
                        </div>
                        <div>
                            <label className="block mb-1 text-sm">
                                Email <span className="text-red-400">*</span>
                            </label>
                            <Field
                                type="email"
                                name="customer_email"
                                value={customerInfo.customer_email}
                                onChange={handleCustomerChange}
                                required
                            />
                        </div>
                        <div>
                            <label className="block mb-1 text-sm">Organization</label>
                            <Field
                                type="text"
                                name="organization"
                                value={customerInfo.organization}
                                onChange={handleCustomerChange}
                            />
                        </div>
                        <div>
                            <label className="block mb-1 text-sm">Phone</label>
                            <Field
                                type="tel"
                                name="customer_phone"
                                value={customerInfo.customer_phone}
                                onChange={handleCustomerChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Items Section */}
                <div className="border-b border-gray-700 pb-4 mb-4">
                    <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-medium text-on-surface-variant">
                            Items to Return <span className="text-red-400">*</span>
                        </h4>
                        <button
                            type="button"
                            onClick={addItem}
                            className={buttonClass('filled', 'sm')}
                        >
                            + Add Item
                        </button>
                    </div>

                    {items.length === 0 ? (
                        <EmptyState variant="area">
                            No items added. Click "Add Item" to start.
                        </EmptyState>
                    ) : (
                        <div className="space-y-4">
                            {items.map((item, index) => {
                                const catalogItem = getCatalogItem(item.catalog_item_id);
                                const requiresSerial = catalogItem?.requires_serial_number;

                                return (
                                    <Panel
                                        key={item.id}
                                        label={`Item ${index + 1}`}
                                        level={3}
                                    >
                                        <div className="flex justify-between items-start mb-3">
                                            <span className="text-sm font-medium text-primary">
                                                Item {index + 1}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => removeItem(item.id)}
                                                className={buttonClass('danger-quiet', 'sm')}
                                            >
                                                Remove
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {/* Item Type */}
                                            <div>
                                                <label className="block mb-1 text-xs text-on-surface-variant">
                                                    Item Type <span className="text-red-400">*</span>
                                                </label>
                                                <Select
                                                    label="Item type"
                                                    value={item.catalog_item_id ? String(item.catalog_item_id) : null}
                                                    onChange={(key) => updateItem(item.id, 'catalog_item_id', key)}
                                                    options={catalog.map(c => ({ key: String(c.id), label: c.name }))}
                                                    placeholder="Select item type..."
                                                />
                                            </div>

                                            {/* Serial Number, Charger ID, or Quantity */}
                                            {catalogItem && (
                                                requiresSerial ? (
                                                    <div>
                                                        <label className="block mb-1 text-xs text-on-surface-variant">
                                                            Serial Number <span className="text-red-400">*</span>
                                                        </label>
                                                        <div className="flex gap-2">
                                                            <Field
                                                                type="text"
                                                                value={item.serial_number}
                                                                onChange={(e) => updateItem(item.id, 'serial_number', e.target.value)}
                                                                className="flex-1"
                                                                placeholder={catalogItem.item_type === 'drone' ? "FF-2024-001234" : "Enter serial"}
                                                            />
                                                            {catalogItem.item_type === 'drone' && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => validateSerial(item.id)}
                                                                    disabled={item.serialValidating || !item.serial_number.trim()}
                                                                    className={buttonClass('quiet')}
                                                                >
                                                                    {item.serialValidating ? "..." : "Validate"}
                                                                </button>
                                                            )}
                                                        </div>
                                                        <Reveal show={item.serialValid === true}>
                                                            <p className="mt-1 text-xs text-green-400">Verified</p>
                                                        </Reveal>
                                                        <Reveal show={!!item.serialError}>
                                                            <p className="mt-1 text-xs text-red-400">{item.serialError}</p>
                                                        </Reveal>
                                                    </div>
                                                ) : catalogItem.item_type === 'charger' ? (
                                                    /* Charger ID field - replaces quantity for chargers */
                                                    <div>
                                                        <label className="block mb-1 text-xs text-on-surface-variant">
                                                            Charger ID <span className="text-red-400">*</span>
                                                            <span className="text-on-surface-variant ml-1">(4 hex digits)</span>
                                                        </label>
                                                        <Field
                                                            type="text"
                                                            value={item.charger_id}
                                                            onChange={(e) => updateItem(item.id, 'charger_id', e.target.value)}
                                                            className="font-mono uppercase"
                                                            placeholder="A1B2"
                                                            maxLength={4}
                                                        />
                                                        <Reveal show={!!item.charger_id && item.charger_id.length !== 4}>
                                                            <p className="mt-1 text-xs text-yellow-400">
                                                                Must be exactly 4 characters (0-9, A-F)
                                                            </p>
                                                        </Reveal>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <label className="block mb-1 text-xs text-on-surface-variant">
                                                            Quantity <span className="text-red-400">*</span>
                                                        </label>
                                                        <Field
                                                            type="number"
                                                            min="1"
                                                            value={item.quantity}
                                                            onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                                                        />
                                                    </div>
                                                )
                                            )}

                                            {/* Issue Category - dynamic based on item type */}
                                            {catalogItem && (
                                                <div>
                                                    <label className="block mb-1 text-xs text-on-surface-variant">
                                                        Issue Category
                                                    </label>
                                                    <Select
                                                        label="Issue category"
                                                        value={item.issue_category}
                                                        onChange={(key) => updateItem(item.id, 'issue_category', key)}
                                                        options={getIssueCategories(catalogItem.item_type).map(cat => ({ key: cat.value, label: cat.label }))}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Issue Description */}
                                        <div className="mt-3">
                                            <label className="block mb-1 text-xs text-on-surface-variant">
                                                Issue Description <span className="text-red-400">*</span>
                                            </label>
                                            <TextArea
                                                value={item.issue_description}
                                                onChange={(e) => updateItem(item.id, 'issue_description', e.target.value)}
                                                rows={2}
                                                placeholder="Describe the problem with this item..."
                                            />
                                        </div>

                                        {/* Flight Log Upload for Drones */}
                                        {catalogItem?.item_type === 'drone' && item.serialValid && (
                                            <div className="mt-3">
                                                <label className="block mb-1 text-xs text-on-surface-variant">
                                                    Flight Log <span className="text-on-surface-variant">(optional, .ulg)</span>
                                                </label>
                                                <FileField
                                                    accept=".ulg"
                                                    onChange={(e) => handleItemLogFile(item.id, e.target.files[0])}
                                                />
                                                <Reveal show={!!item.log_file}>
                                                    <p className="mt-1 text-xs text-on-surface-variant">
                                                        Selected: {item.log_file?.name}
                                                    </p>
                                                </Reveal>
                                                <Reveal show={logUploadStatus[item.id] === 'uploading'}>
                                                    <p className="mt-1 text-xs text-yellow-400 flex items-center gap-1">
                                                        <LogoSpinner size="inline" /> Uploading log...
                                                    </p>
                                                </Reveal>
                                                <Reveal show={logUploadStatus[item.id] === 'success'}>
                                                    <Alert tone="success" variant="text" className="mt-1 text-xs">
                                                        Log uploaded successfully
                                                    </Alert>
                                                </Reveal>
                                                <Reveal show={logUploadStatus[item.id] === 'error'}>
                                                    <Alert variant="text" className="mt-1 text-xs">
                                                        Log upload failed
                                                    </Alert>
                                                </Reveal>
                                            </div>
                                        )}

                                        {/* Shipping notes from catalog */}
                                        {catalogItem?.shipping_notes && (
                                            <Alert tone="warning" variant="note" className="mt-2" title="Shipping note:">
                                                {catalogItem.shipping_notes}
                                            </Alert>
                                        )}

                                        {/* Hazmat warning */}
                                        {catalogItem?.requires_hazmat && (
                                            <Alert tone="warning" variant="note" className="mt-2">
                                                This item requires hazmat handling. Additional shipping instructions will be provided.
                                            </Alert>
                                        )}
                                    </Panel>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Shipping Address Section */}
                <div className="border-b border-gray-700 pb-4 mb-4">
                    <h4 className="text-sm font-medium text-on-surface-variant mb-3">
                        Return Shipping Address
                    </h4>
                    <div className="mb-3">
                        <label className="block mb-1 text-sm">Address Line 1</label>
                        <Field
                            type="text"
                            name="address_line1"
                            value={customerInfo.address_line1}
                            onChange={handleCustomerChange}
                            placeholder="Street address"
                        />
                    </div>
                    <div className="mb-3">
                        <label className="block mb-1 text-sm">Address Line 2</label>
                        <Field
                            type="text"
                            name="address_line2"
                            value={customerInfo.address_line2}
                            onChange={handleCustomerChange}
                            placeholder="Apt, suite, etc."
                        />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="col-span-2 md:col-span-1">
                            <label className="block mb-1 text-sm">City</label>
                            <Field
                                type="text"
                                name="city"
                                value={customerInfo.city}
                                onChange={handleCustomerChange}
                            />
                        </div>
                        <div>
                            <label className="block mb-1 text-sm">State</label>
                            <Field
                                type="text"
                                name="state"
                                value={customerInfo.state}
                                onChange={handleCustomerChange}
                                maxLength={2}
                            />
                        </div>
                        <div>
                            <label className="block mb-1 text-sm">ZIP</label>
                            <Field
                                type="text"
                                name="zip"
                                value={customerInfo.zip}
                                onChange={handleCustomerChange}
                            />
                        </div>
                        <div className="col-span-2 md:col-span-1">
                            <label className="block mb-1 text-sm">Country</label>
                            <Field
                                type="text"
                                name="country"
                                value={customerInfo.country}
                                onChange={handleCustomerChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Summary */}
                <Reveal show={items.length > 0}>
                    <div className="mb-4 p-3 bg-surface-container-high rounded">
                        <div className="text-sm text-on-surface-variant">
                            <span className="font-medium text-white">{items.length}</span> item(s) to submit
                        </div>
                    </div>
                </Reveal>

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={submitting || uploadingLogs || items.length === 0}
                    className={`${buttonClass('filled', 'lg')} w-full`}
                >
                    {submitting ? "Submitting..." : uploadingLogs ? "Uploading Flight Logs..." : "Submit RMA Request"}
                </button>

                <p className="text-center text-on-surface-variant text-sm mt-4">
                    Already have an RMA?{" "}
                    <Link to="/rma/status" className="text-primary hover:underline">
                        Check Status
                    </Link>
                </p>
            </form>
        </div>
    );
};

export default RMASubmit;
